/**
 * Column editor for a REPEATING GROUP (`type: 'repeatable'`).
 *
 * A repeating group stores an array of plain row objects; each column's `path` is the KEY
 * within that row, not a dotted matter path. Shared by Admin → Field Builder (adding AND
 * editing a group) and the intake form's inline layout editor.
 *
 * THE KEY IS ASSIGNED ONCE AND NEVER REWRITTEN BY A LABEL EDIT. Renaming "Phone" to
 * "Mobile" must not move where existing rows stored their value — same rule (and same
 * hard-won reason) as the stored values in OptionRowsEditor.
 *
 * `onChange` is always called with a plain array.
 */

import { SUB_FIELD_TYPES, subFieldKey, subFieldTypeNeedsOptions } from '../lib/fieldLayout';
import OptionRowsEditor from './OptionRowsEditor';

export default function SubFieldRowsEditor({ fields, onChange, legend = 'Columns', autoSlug = false }) {
  const rows = Array.isArray(fields) ? fields : [];
  const patch = (index, updates) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...updates } : row)));

  return (
    <fieldset className="min-w-[22rem] text-xs text-slate-600 dark:text-slate-300">
      <legend className="mb-0.5">{legend}</legend>
      <p className="mb-1 max-w-md text-slate-500 dark:text-slate-400">
        Each column becomes one input on every entry of the group.
      </p>
      <p className="mb-1 max-w-md text-amber-700 dark:text-amber-300">
        Renaming a column is safe. <strong>Removing one, or changing its type, affects
        answers already saved on existing matters</strong> — a removed column’s answers
        stop appearing on the form and in matter records.
      </p>
      <div className="space-y-1">
        {rows.map((row, index) => (
          <div key={index} className="rounded border border-slate-200 p-1.5 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-1">
              <input
                className="w-36 rounded border border-slate-300 px-2 py-1 text-sm dark:border-slate-600"
                value={row.label || ''}
                placeholder="Column label"
                aria-label={`Column ${index + 1} label`}
                onChange={(event) => {
                  const label = event.target.value;
                  patch(index, { label });
                }}
                onBlur={() => {
                  // Fill an EMPTY key from the finished label, once. Never touches a key
                  // that already exists — that is where row data lives.
                  if (!row.path && row.label) {
                    patch(index, {
                      path: subFieldKey(
                        row.label,
                        rows.filter((_, i) => i !== index).map((r) => r.path).filter(Boolean)
                      ),
                    });
                  }
                }}
              />
              <select
                className="rounded border border-slate-300 px-1 py-1 dark:border-slate-600"
                value={row.type || 'text'}
                aria-label={`Column ${index + 1} type`}
                onChange={(event) => {
                  const type = event.target.value;
                  // Seed a blank choice row when switching TO a choice column: the live
                  // preview renders this column immediately, and a choice control with no
                  // options array is a crash rather than an empty dropdown.
                  patch(index, {
                    type,
                    ...(subFieldTypeNeedsOptions(type) && !Array.isArray(row.options)
                      ? { options: [{ label: '', value: '' }] }
                      : null),
                  });
                }}
              >
                {SUB_FIELD_TYPES.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              {row.path && (
                <code className="text-[11px] text-slate-400 dark:text-slate-500" title="Stored key for this column">
                  {row.path}
                </code>
              )}
              <button
                type="button"
                onClick={() => onChange(rows.filter((_, i) => i !== index))}
                className="px-1 text-red-600 hover:underline dark:text-red-400"
                aria-label={`Remove column ${index + 1}`}
              >
                Remove
              </button>
            </div>
            {subFieldTypeNeedsOptions(row.type) && (
              <div className="mt-1 pl-2">
                <OptionRowsEditor
                  options={row.options}
                  minimumOptions={row.type === 'radio' ? 2 : 1}
                  autoSlug={autoSlug}
                  legend={`Choices for “${row.label || 'this column'}”`}
                  onChange={(next) => patch(index, { options: next })}
                />
              </div>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={() => onChange([...rows, { path: '', label: '', type: 'text' }])}
        className="mt-1 text-firm-700 hover:underline dark:text-firm-100"
      >
        + Add column
      </button>
    </fieldset>
  );
}
